import { css } from '@mui/material';
import { EChartsOption, EChartsType, init as echartsInit } from 'echarts';
import { useEffect, useRef } from 'react';
import { ECHARTS_V5_THEME } from './echarts-v5-theme';

const chartWrapperCss = css`
  display: flex;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

export type EChartWrapperProps = {
  lazyUpdate?: boolean;
  notMerge?: boolean;
  onChartReady?: (chart: EChartsType) => void;
  options: EChartsOption;
};

export function EChartWrapper(props: EChartWrapperProps) {
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<EChartsType | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  // Effect to handle echarts init
  useEffect(() => {
    const { options, notMerge, lazyUpdate, onChartReady } = props;
    const container = chartContainerRef.current;

    if (!container) {
      return;
    }

    if (!chartRef.current) {
      chartRef.current = echartsInit(container, ECHARTS_V5_THEME);
      onChartReady?.(chartRef.current);
    }
    chartRef.current.setOption(options, notMerge, lazyUpdate);
  }, [props]);

  // Effect to handle resizing.
  useEffect(() => {
    const container = chartContainerRef.current;
    const chart = chartRef.current;

    if (!container || !chart) {
      return;
    }

    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
    }

    resizeObserverRef.current = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentBoxSize) {
          chart.resize();
        }
      }
    });

    resizeObserverRef.current.observe(container);

    return () => resizeObserverRef.current?.disconnect();
  }, []);

  return <div ref={chartContainerRef} css={chartWrapperCss} />;
}
